import { useEffect, useState } from "react";
import { Star, Send } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

export default function LeaveReviewSection({ productId = null, compact = false }) {
  const [name, setName] = useState("");
  const [city, setCity] = useState("");
  const [rating, setRating] = useState(5);
  const [hover, setHover] = useState(0);
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(true);

  const loadReviews = () => {
    setLoadingReviews(true);
    const params = productId ? { product_id: productId } : { featured: true };
    api
      .get("/reviews", { params })
      .then((r) => setReviews(r.data || []))
      .catch(() => setReviews([]))
      .finally(() => setLoadingReviews(false));
  };

  useEffect(() => {
    loadReviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  const avg = reviews.length
    ? (reviews.reduce((s, r) => s + (r.rating || 5), 0) / reviews.length).toFixed(1)
    : null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !text.trim()) {
      toast.error("Please add your name and review");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/reviews", {
        name: name.trim(),
        city: city.trim(),
        rating,
        text: text.trim(),
        product_id: productId,
      });
      setName("");
      setCity("");
      setText("");
      setRating(5);
      toast.success("Thank you for the love.");
      loadReviews();
    } catch {
      toast.error("Could not submit. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      return d.toLocaleDateString("en-IN", { month: "short", year: "numeric" });
    } catch {
      return "";
    }
  };

  return (
    <section
      data-testid="leave-review-section"
      className={`bg-[#0A0A0A] ${compact ? "py-14" : "py-20"} border-t border-[#262626]`}
    >
      <div className="max-w-4xl mx-auto px-6 md:px-10">
        <div className="text-center">
          <div
            className="font-ui mb-3"
            style={{ fontSize: 12, fontWeight: 600, color: "#F5D565", letterSpacing: "0.4em", textTransform: "uppercase" }}
          >
            ✦ Reviews
          </div>
          <h2
            className="font-serif-display"
            style={{ fontSize: compact ? 40 : 48, color: "#FFFFFF", lineHeight: 1, fontWeight: 500 }}
          >
            Worn &amp; <em className="not-italic gold-text font-light">Loved.</em>
          </h2>
          {avg && (
            <div className="flex items-center justify-center gap-3 mt-5">
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    size={16}
                    fill={i < Math.round(avg) ? "#F5D565" : "transparent"}
                    stroke="#F5D565"
                  />
                ))}
              </div>
              <span className="font-ui" style={{ fontSize: 16, color: "#FFFFFF", fontWeight: 600 }}>
                {avg}
              </span>
              <span className="font-body" style={{ fontSize: 13, color: "rgba(255,255,255,0.6)" }}>
                · {reviews.length} review{reviews.length === 1 ? "" : "s"}
              </span>
            </div>
          )}
        </div>

        {/* Submitted reviews list — max 3, featured-selected by admin */}
        {!loadingReviews && reviews.length > 0 && (
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="reviews-list">
            {reviews.slice(0, 3).map((r) => (
              <div
                key={r.id}
                data-testid={`review-${r.id}`}
                className="border border-[#262626] p-5"
                style={{ background: "#111111" }}
              >
                <div className="flex items-center gap-1 mb-2">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      size={12}
                      fill={i < (r.rating || 5) ? "#F5D565" : "transparent"}
                      stroke="#F5D565"
                    />
                  ))}
                </div>
                <p className="font-body" style={{ fontSize: 15, color: "rgba(255,255,255,0.88)", lineHeight: 1.55 }}>
                  "{r.text}"
                </p>
                <div className="mt-4 flex items-center gap-2 flex-wrap">
                  <span className="font-ui" style={{ fontSize: 13, color: "#FFFFFF", fontWeight: 600 }}>
                    {r.name}
                  </span>
                  {r.city && (
                    <span className="font-body" style={{ fontSize: 12, color: "rgba(255,255,255,0.5)" }}>
                      · {r.city}
                    </span>
                  )}
                  {r.created_at && (
                    <span className="font-body ml-auto" style={{ fontSize: 11, color: "rgba(255,255,255,0.4)" }}>
                      {formatDate(r.created_at)}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {reviews.length > 3 && (
          <div className="mt-6 text-center">
            <span
              className="font-body"
              style={{ fontSize: 13, color: "rgba(255,255,255,0.5)", letterSpacing: "0.05em" }}
            >
              + {reviews.length - 3} more
            </span>
          </div>
        )}

        {/* Leave a review */}
        <div className="mt-14 max-w-2xl mx-auto">
          <p
            className="font-body text-center"
            style={{ fontSize: 16, color: "rgba(255,255,255,0.72)", lineHeight: 1.55 }}
          >
            Leave a review — let her hear how she made you feel.
          </p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-5" data-testid="review-form">
            <div className="flex items-center justify-center gap-2">
              {[1, 2, 3, 4, 5].map((n) => {
                const filled = (hover || rating) >= n;
                return (
                  <button
                    key={n}
                    type="button"
                    onMouseEnter={() => setHover(n)}
                    onMouseLeave={() => setHover(0)}
                    onClick={() => setRating(n)}
                    data-testid={`review-star-${n}`}
                    className="transition-transform hover:scale-110"
                    style={{ minWidth: 44, minHeight: 44, display: "inline-flex", alignItems: "center", justifyContent: "center" }}
                    aria-label={`${n} stars`}
                  >
                    <Star size={26} strokeWidth={1.5} fill={filled ? "#F5D565" : "transparent"} stroke="#F5D565" />
                  </button>
                );
              })}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Your Name" value={name} onChange={setName} testid="review-name" placeholder="Riya" required />
              <Field label="City (optional)" value={city} onChange={setCity} testid="review-city" placeholder="Delhi" />
            </div>

            <label className="block">
              <span className="font-ui" style={{ fontSize: 12, fontWeight: 600, color: "#F5D565", letterSpacing: "0.18em", textTransform: "uppercase" }}>
                Your Review
              </span>
              <textarea
                data-testid="review-text"
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={4}
                placeholder="What did you love about it?"
                className="font-body w-full mt-3 bg-transparent border border-[#262626] focus:border-[#F5D565] outline-none p-4 resize-none"
                style={{ fontSize: 16, color: "#FFFFFF", lineHeight: 1.5 }}
              />
            </label>

            <div className="text-center">
              <button
                type="submit"
                disabled={submitting}
                data-testid="review-submit"
                className="btn-gold disabled:opacity-50"
              >
                {submitting ? "Sending…" : "Submit Review"} <Send size={14} strokeWidth={1.5} />
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}

const Field = ({ label, value, onChange, testid, placeholder, required }) => (
  <label className="block">
    <span className="font-ui" style={{ fontSize: 12, fontWeight: 600, color: "#F5D565", letterSpacing: "0.18em", textTransform: "uppercase" }}>
      {label}
    </span>
    <input
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      required={required}
      className="font-body w-full mt-3 bg-transparent border-b border-[#262626] focus:border-[#F5D565] outline-none"
      style={{ fontSize: 16, color: "#FFFFFF", padding: "10px 0", minHeight: 44, fontWeight: 500 }}
    />
  </label>
);
